import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { stores, deals, categories } from '../data/mockData';
import { ShoppingCart, Ticket, ChevronRight, Copy, ExternalLink, Star, Clock, Eye, ThumbsUp, Timer, Flame, Heart } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import DealDialog from '../components/DealDialog';
import DealStatsInteractive from '../components/DealStatsInteractive';
import StoreRatingInteractive from '../components/StoreRatingInteractive';
import { useAuth } from '../context/AuthContext';

const StorePage = () => {
  const { slug } = useParams();
  const store = stores.find((s) => s.slug === slug);
  const storeDeals = deals.filter((d) => d.store === slug);
  const [revealedCoupons, setRevealedCoupons] = useState({});
  const [activeTab, setActiveTab] = useState('all');
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ hours: 12, minutes: 45, seconds: 30 });
  const [loadingFavorite, setLoadingFavorite] = useState(null);
  
  const { isAuthenticated, isFavorite, addFavorite, removeFavorite } = useAuth();

  const handleToggleFavorite = async (e, dealId) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast.error('Please sign in to save favorites');
      return;
    }
    
    setLoadingFavorite(dealId);
    try {
      if (isFavorite(dealId)) {
        await removeFavorite(dealId);
        toast.success('Removed from favorites');
      } else {
        await addFavorite(dealId);
        toast.success('Added to favorites!');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoadingFavorite(null);
    }
  };

  // Countdown timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        let { hours, minutes, seconds } = prev;
        
        if (seconds > 0) {
          seconds--;
        } else if (minutes > 0) {
          minutes--;
          seconds = 59;
        } else if (hours > 0) {
          hours--;
          minutes = 59;
          seconds = 59;
        } else {
          // Reset timer when it reaches 0
          hours = 23;
          minutes = 59;
          seconds = 59;
        }
        
        return { hours, minutes, seconds };
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  const formatTime = (num) => String(num).padStart(2, '0');

  const tabs = [
    { id: 'all', label: 'All' },
    { id: 'deals', label: 'Deals' },
    { id: 'coupons', label: 'Coupons' },
    { id: 'sales', label: 'Sales' },
  ];

  // Get Deal of the Day - highest discount deal
  const dealOfTheDay = useMemo(() => {
    if (storeDeals.length === 0) return null;
    return storeDeals.reduce((max, deal) => deal.discount > max.discount ? deal : max, storeDeals[0]);
  }, [storeDeals]);

  const filteredDeals = storeDeals.filter((deal) => {
    if (deal.id === dealOfTheDay?.id) return false;
    if (activeTab === 'all') return true;
    if (activeTab === 'deals') return deal.buttonType === 'buy';
    if (activeTab === 'coupons') return deal.buttonType === 'coupon';
    if (activeTab === 'sales') return deal.discount >= 25;
    return true;
  });

  const storeLogos = {
    aliexpress: { text: 'AliExpress', color: '#e62e04', font: 'font-bold text-lg' },
    asos: { text: 'asos', color: '#2d2d2d', font: 'font-bold text-2xl lowercase' },
    ebay: { colors: ['#e53238', '#0064d2', '#f5af02', '#86b817'] },
    gogreen: { text: 'Go Green', color: '#228b22', icon: '🌲' },
    iherb: { text: 'iHerb', color: '#68a648', font: 'font-bold text-xl' },
    laroche: { text: 'LA ROCHE-POSAY', color: '#0066b2', font: 'font-bold text-sm', subtitle: 'LABORATOIRE' },
    panasonic: { text: 'Panasonic', color: '#0066b2', font: 'font-bold text-xl' },
    salamander: { text: 'SALAMANDER', color: '#ff6b00', font: 'font-bold text-sm tracking-wider' },
    vichy: { text: 'VICHY', color: '#004976', font: 'font-bold text-xl', subtitle: 'LABORATOIRES' },
    'yves-roche': { text: 'YVES ROCHER', color: '#006633', font: 'font-bold text-sm', subtitle: 'FRANCE' },
    amazon: { text: 'amazon', color: '#ff9900', font: 'font-bold text-xl' },
    walmart: { text: 'Walmart', color: '#0071ce', font: 'font-bold text-xl' },
    target: { text: 'Target', color: '#cc0000', font: 'font-bold text-xl' },
    bestbuy: { text: 'Best Buy', color: '#0046be', font: 'font-bold text-lg' },
    // Fashion stores
    zara: { text: 'ZARA', color: '#000000', font: 'font-bold text-2xl tracking-[0.2em]' },
    hm: { text: 'H&M', color: '#e50010', font: 'font-bold text-2xl' },
    nike: { text: 'NIKE', color: '#000000', swoosh: true },
    nordstrom: { text: 'NORDSTROM', color: '#000000', font: 'font-medium text-sm tracking-[0.1em]' },
    shein: { text: 'SHEIN', color: '#000000', font: 'font-bold text-xl tracking-wide' },
    // Cosmetics stores
    sephora: { text: 'SEPHORA', color: '#000000', font: 'font-bold text-lg tracking-[0.08em]' },
    ulta: { text: 'ULTA', color: '#000', font: 'font-extrabold text-xl', subtitleColor: '#ff6900', subtitle: 'beauty' },
    mac: { text: 'M·A·C', color: '#000000', font: 'font-bold text-2xl' },
    'charlotte-tilbury': { text: 'Charlotte Tilbury', color: '#c9a961', font: 'text-lg italic' },
    glossier: { text: 'Glossier', color: '#000', font: 'font-medium text-xl italic' },
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  };

  const handleBuyNow = (deal) => {
    setSelectedDeal({ ...deal, storeName: store?.name });
    setDialogOpen(true);
  };

  const handleRevealCoupon = async (deal) => {
    setRevealedCoupons(prev => ({ ...prev, [deal.id]: true }));
    const copied = await copyToClipboard(deal.couponCode);
    toast.success(
      <div className="flex flex-col">
        <span className="font-semibold flex items-center gap-2">
          <Copy size={16} /> Coupon Code {copied ? 'Copied!' : 'Revealed!'}
        </span>
        <span className="text-lg font-mono bg-gray-100 px-2 py-1 rounded mt-1">{deal.couponCode}</span>
      </div>,
      { duration: 5000 }
    );
  };

  const handleGoToStore = (deal) => {
    setSelectedDeal({ ...deal, storeName: store?.name });
    setDialogOpen(true);
  };

  const renderStoreLogo = () => {
    const logoInfo = storeLogos[slug];
    if (!logoInfo) return <span className="text-3xl font-bold text-gray-700">{store?.name}</span>;

    if (slug === 'ebay') {
      return (
        <span className="text-4xl font-bold">
          {'ebay'.split('').map((letter, i) => (
            <span key={i} style={{ color: logoInfo.colors[i] }}>{letter}</span>
          ))}
        </span>
      );
    }

    if (slug === 'gogreen') {
      return (
        <div className="flex flex-col items-center">
          <div className="text-teal-700 text-4xl mb-1">🌲</div>
          <span className="text-2xl font-bold" style={{ color: logoInfo.color }}>Go Green</span>
        </div>
      );
    }

    if (slug === 'nike') {
      return (
        <svg viewBox="0 0 24 24" className="w-16 h-16 fill-black">
          <path d="M21 8.719L7.836 14.303C6.74 14.768 5.818 15 5.075 15c-.836 0-1.445-.295-1.819-.884-.485-.76-.273-1.982.559-3.272.494-.754 1.122-1.446 1.734-2.108-.144.234-.258.48-.322.718-.156.559-.123 1.064.129 1.503.373.65 1.108.982 2.182.982.57 0 1.223-.117 1.947-.351L21 8.719z"/>
        </svg>
      );
    }

    if (slug === 'ulta') {
      return (
        <div className="flex flex-col items-center">
          <span className="text-3xl font-extrabold" style={{ color: logoInfo.color }}>ULTA</span>
          <span className="text-sm font-bold" style={{ color: logoInfo.subtitleColor }}>beauty</span>
        </div>
      );
    }

    if (logoInfo.subtitle && slug !== 'ulta') {
      return (
        <div className="flex flex-col items-center">
          <span className={`${logoInfo.font || 'text-2xl font-bold'}`} style={{ color: logoInfo.color }}>
            {logoInfo.text}
          </span>
          <span className="text-xs text-gray-500 tracking-wider">{logoInfo.subtitle}</span>
        </div>
      );
    }

    return (
      <span className={`${logoInfo.font || 'text-3xl font-bold'}`} style={{ color: logoInfo.color }}>
        {logoInfo.text}
      </span>
    );
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalf = rating % 1 >= 0.5;
    
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<Star key={i} size={16} className="fill-yellow-400 text-yellow-400" />);
      } else if (i === fullStars && hasHalf) {
        stars.push(<Star key={i} size={16} className="fill-yellow-400/50 text-yellow-400" />);
      } else {
        stars.push(<Star key={i} size={16} className="text-gray-300" />);
      }
    }
    return stars;
  };

  const getBadgeColor = (badge) => {
    if (!badge) return 'bg-gray-500';
    if (badge.includes('Best seller')) return 'bg-teal-700';
    if (badge.includes('Hot') || badge.includes('Mega')) return 'bg-red-500';
    if (badge.includes('Best price') || badge.includes('Best value')) return 'bg-blue-500';
    if (badge.includes('Clearance') || badge.includes('Limited')) return 'bg-purple-500';
    return 'bg-gray-500';
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <Header />
      
      <main className="flex-1">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center text-sm text-gray-600">
              <Link to="/" className="hover:text-[#E53A7F]">Home</Link>
              <ChevronRight size={14} className="mx-2" />
              <Link to="/stores" className="hover:text-[#E53A7F]">All Stores</Link>
              <ChevronRight size={14} className="mx-2" />
              <span className="text-gray-800 font-medium">{store?.name || 'Store'}</span>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar - Store Info */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm p-6 sticky top-20">
                {/* Store Logo */}
                <div className="text-center mb-4">
                  <h1 className="text-2xl font-bold text-gray-800 mb-4">{store?.name}</h1>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-4">
                    {renderStoreLogo()}
                  </div>
                </div>

                {/* Cashback Info */}
                <div className="bg-[#E53A7F]/10 rounded-lg p-3 mb-4">
                  <p className="text-[#E53A7F] font-semibold text-center">{store?.cashback}</p>
                </div>

                {/* Interactive Store Rating */}
                <div className="flex flex-col items-center mb-4">
                  <StoreRatingInteractive 
                    storeSlug={slug} 
                    initialRating={store?.rating || 4} 
                    initialVotes={store?.votes || 0} 
                  />
                </div>

                {/* Go to Shop Button */}
                <a
                  href={store?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-[#E53A7F] text-white text-center py-3 rounded-lg font-medium hover:bg-[#C42D69] transition-colors"
                >
                  Go to shop
                </a>

                {/* Store Description */}
                <p className="text-gray-600 text-sm mt-4 text-center">
                  {store?.description}
                </p>

                {/* Stats */}
                <div className="mt-4 pt-4 border-t">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Total Deals:</span>
                    <span className="font-semibold text-gray-700">{storeDeals.length}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-2">
                    <span className="text-gray-500">Coupons:</span>
                    <span className="font-semibold text-gray-700">
                      {storeDeals.filter(d => d.buttonType === 'coupon').length}
                    </span>
                  </div>
                </div>

                {/* Categories Widget */}
                <div className="mt-6 pt-4 border-t">
                  <h3 className="font-bold text-gray-800 mb-3 text-sm">Categories</h3>
                  <div className="flex flex-wrap gap-1">
                    {categories.slice(0, 8).map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/category/${cat.slug}`}
                        className="text-xs text-[#E53A7F] hover:underline"
                      >
                        {cat.name},
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content - Deals */}
            <div className="lg:col-span-3">
              {/* Deal of the Day Section */}
              {dealOfTheDay && (
                <div className="bg-white rounded-lg shadow-sm mb-6 overflow-hidden">
                  <div className="bg-gradient-to-r from-[#E53A7F] to-[#C42D69] px-4 py-3 flex items-center gap-2">
                    <Flame className="text-yellow-400" size={20} />
                    <span className="text-white font-bold">Deal of the Day</span>
                    <div className="ml-auto flex items-center gap-2 text-white text-sm font-mono">
                      <Timer size={16} className="text-yellow-400" />
                      <span className="bg-white/20 px-2 py-0.5 rounded">{formatTime(timeLeft.hours)}</span>
                      <span>:</span>
                      <span className="bg-white/20 px-2 py-0.5 rounded">{formatTime(timeLeft.minutes)}</span>
                      <span>:</span>
                      <span className="bg-white/20 px-2 py-0.5 rounded">{formatTime(timeLeft.seconds)}</span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex flex-col md:flex-row gap-6">
                      {/* Image */}
                      <div className="md:w-64 flex-shrink-0">
                        <div className="relative">
                          <img
                            src={dealOfTheDay.image}
                            alt={dealOfTheDay.title}
                            className="w-full h-48 md:h-64 object-contain bg-gray-50 rounded-lg"
                          />
                          <div className="absolute top-2 right-2 bg-red-500 text-white text-lg font-bold px-3 py-1 rounded">
                            -{dealOfTheDay.discount}%
                          </div>
                          {/* Favorite Button */}
                          <button
                            onClick={(e) => handleToggleFavorite(e, dealOfTheDay.id)}
                            disabled={loadingFavorite === dealOfTheDay.id}
                            className={`absolute top-2 left-2 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                              isFavorite(dealOfTheDay.id) 
                                ? 'bg-red-500 text-white' 
                                : 'bg-white text-gray-400 hover:text-red-500 shadow-md'
                            }`}
                            data-testid={`favorite-btn-dotd-${dealOfTheDay.id}`}
                          >
                            {loadingFavorite === dealOfTheDay.id ? (
                              <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Heart size={20} fill={isFavorite(dealOfTheDay.id) ? 'currentColor' : 'none'} />
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1">
                        <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-3">
                          {dealOfTheDay.title}
                        </h2>
                        
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-3xl font-bold text-[#E53A7F]">${dealOfTheDay.salePrice?.toFixed(2)}</span>
                          <span className="text-xl text-gray-400 line-through">${dealOfTheDay.originalPrice?.toFixed(2)}</span>
                        </div>

                        <p className="text-[#E53A7F] font-medium mb-3">{dealOfTheDay.cashback}</p>
                        <p className="text-gray-600 mb-4">{dealOfTheDay.description}</p>

                        {/* Interactive Stats */}
                        <div className="mb-4">
                          <DealStatsInteractive 
                            dealId={dealOfTheDay.id} 
                            initialViews={dealOfTheDay.views} 
                          />
                        </div>

                        <button
                          onClick={() => handleBuyNow(dealOfTheDay)}
                          className="bg-[#E53A7F] text-white px-8 py-3 rounded-lg font-medium hover:bg-[#C42D69] transition-colors flex items-center gap-2"
                        >
                          <ShoppingCart size={18} />
                          Buy It Now
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tabs */}
              <div className="bg-white rounded-lg shadow-sm mb-4">
                <div className="flex border-b overflow-x-auto">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-6 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                        activeTab === tab.id
                          ? 'text-[#E53A7F] border-b-2 border-[#E53A7F]'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Deals List */}
              <div className="space-y-4">
                {filteredDeals.map((deal) => (
                  <div
                    key={deal.id}
                    className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <div className="flex flex-col md:flex-row">
                      {/* Vote/Discount Badge */}
                      <div className="md:w-16 bg-gray-50 flex md:flex-col items-center justify-center p-3 md:p-4 border-b md:border-b-0 md:border-r">
                        <span className="text-2xl md:text-3xl font-bold text-[#E53A7F]">{deal.discount}</span>
                        <span className="text-xs text-gray-500 ml-1 md:ml-0">%</span>
                      </div>

                      {/* Image */}
                      <div className="md:w-40 p-4 flex items-center justify-center bg-gray-50 relative">
                        <img
                          src={deal.image}
                          alt={deal.title}
                          className="w-24 h-24 md:w-32 md:h-32 object-contain"
                          onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=300&h=300&fit=crop';
                          }}
                        />
                        {/* Favorite Button */}
                        <button
                          onClick={(e) => handleToggleFavorite(e, deal.id)}
                          disabled={loadingFavorite === deal.id}
                          className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                            isFavorite(deal.id) 
                              ? 'bg-red-500 text-white' 
                              : 'bg-white text-gray-400 hover:text-red-500 shadow-sm'
                          }`}
                          data-testid={`favorite-btn-${deal.id}`}
                        >
                          {loadingFavorite === deal.id ? (
                            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Heart size={16} fill={isFavorite(deal.id) ? 'currentColor' : 'none'} />
                          )}
                        </button>
                      </div>

                      {/* Content */}
                      <div className="flex-1 p-4">
                        {/* Badges */}
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {deal.badge && (
                            <span className={`${getBadgeColor(deal.badge)} text-white text-xs px-2 py-0.5 rounded`}>
                              {deal.badge}
                            </span>
                          )}
                          <span className="text-gray-400 text-xs flex items-center gap-1">
                            <Clock size={12} />
                            1 year ago
                          </span>
                          <span className="text-gray-400 text-xs flex items-center gap-1">
                            <Eye size={12} />
                            {deal.views}
                          </span>
                        </div>

                        {/* Title */}
                        <h3 className="text-lg font-semibold text-gray-800 hover:text-[#E53A7F] mb-2 line-clamp-2">
                          {deal.title}
                        </h3>

                        {/* Price */}
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-xl font-bold text-gray-800">${deal.salePrice?.toFixed(2)}</span>
                          <span className="text-gray-400 line-through">${deal.originalPrice?.toFixed(2)}</span>
                        </div>

                        {/* Cashback */}
                        <p className="text-[#E53A7F] text-sm mb-3">{deal.cashback}</p>

                        {/* Button */}
                        <div className="flex flex-col sm:flex-row gap-2">
                          {deal.buttonType === 'coupon' ? (
                            revealedCoupons[deal.id] ? (
                              <>
                                <div className="flex-1 bg-gray-100 border-2 border-dashed border-[#E53A7F] text-[#E53A7F] py-2.5 px-4 rounded font-mono text-center font-bold">
                                  {deal.couponCode}
                                </div>
                                <button 
                                  onClick={() => handleGoToStore(deal)}
                                  className="bg-[#E53A7F] text-white py-2.5 px-6 rounded font-medium text-sm hover:bg-[#C42D69] transition-colors flex items-center justify-center gap-2"
                                >
                                  <ExternalLink size={16} />
                                  Go to Store
                                </button>
                              </>
                            ) : (
                              <button 
                                onClick={() => handleRevealCoupon(deal)}
                                className="flex-1 bg-orange-500 text-white py-2.5 px-6 rounded font-medium text-sm hover:bg-orange-600 transition-colors flex items-center justify-center gap-2"
                              >
                                <Ticket size={16} />
                                Reveal coupon
                              </button>
                            )
                          ) : (
                            <button 
                              onClick={() => handleBuyNow(deal)}
                              className="flex-1 bg-[#E53A7F] text-white py-2.5 px-6 rounded font-medium text-sm hover:bg-[#C42D69] transition-colors flex items-center justify-center gap-2"
                            >
                              <ShoppingCart size={16} />
                              Buy It Now
                            </button>
                          )}
                        </div>

                        {/* Interactive Stats */}
                        <div className="mt-3 pt-3 border-t">
                          <DealStatsInteractive dealId={deal.id} initialViews={deal.views} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {filteredDeals.length === 0 && storeDeals.length > 1 && (
                  <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                    <p className="text-gray-500">No deals found in this category.</p>
                    <button
                      onClick={() => setActiveTab('all')}
                      className="text-[#E53A7F] hover:underline mt-2"
                    >
                      View all deals
                    </button>
                  </div>
                )}

                {storeDeals.length === 0 && (
                  <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                    <p className="text-gray-500">No deals available from this store yet.</p>
                    <Link to="/" className="text-[#E53A7F] hover:underline mt-2 inline-block">
                      Browse all deals
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* Deal Dialog */}
      <DealDialog
        deal={selectedDeal}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        storeName={selectedDeal?.storeName}
      />
    </div>
  );
};

export default StorePage;
